// Anti-clickjacking guard.
//
// `frame-ancestors` is ignored when the CSP arrives in a meta tag, and Puter
// hosting serves no response headers we control, so the only defense available
// is refusing to run when framed by an origin we do not trust. Without it a
// hostile page can iframe the vault under an invisible overlay and trick a
// signed-in user into clicking "Show"/"Copy" on a credential, or "Overwrite
// cloud" in the first-sync dialog.
//
// A blanket "never run framed" rule also broke the app's own home: Puter's GUI
// launches installed apps in an iframe (https://puter.com/app/vault), so
// opening the vault from Puter showed nothing but the refusal message. Hence an
// allowlist instead of a flat no.
//
// Exact origins only — a wildcard over *.puter.com would let any other app or
// user subdomain frame the vault, which is precisely the attack this guard
// exists to stop.
export const ALLOWED_FRAME_ANCESTORS = Object.freeze([
  'https://puter.com',
  'https://www.puter.com',
]);

function originOf(url) {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/**
 * Snapshot the framing facts from a window. Everything is read defensively:
 * cross-origin access throws, and a thrown read must read as "framed", never as
 * "top level".
 *
 * @param {Window} win
 * @returns {{framed: boolean, ancestorOrigins: string[]|null, referrer: string}}
 */
export function readFrameEnv(win) {
  let framed = true;
  try {
    framed = win.top !== win.self;
  } catch {
    framed = true; // cross-origin `top` access throws only when framed
  }

  let ancestorOrigins = null;
  try {
    const list = win.location.ancestorOrigins;
    if (list && typeof list.length === 'number') ancestorOrigins = Array.from(list);
  } catch {
    ancestorOrigins = null;
  }

  let referrer = '';
  try {
    referrer = win.document.referrer || '';
  } catch {
    referrer = '';
  }

  return { framed, ancestorOrigins, referrer };
}

/**
 * Decide whether the app may run in this framing context.
 *
 * `location.ancestorOrigins` (Chrome/Safari) is the strong check: it is
 * browser-supplied, lists every ancestor, and cannot be forged by the embedder.
 * Firefox does not implement it, so there we fall back to the referrer origin —
 * an embedder still cannot forge it, but it only describes the immediate
 * navigation initiator, so a puter.com open-redirect nested inside a hostile
 * page would slip through on that browser. Both paths fail closed: no usable
 * evidence means no run.
 *
 * @param {{framed: boolean, ancestorOrigins?: string[]|null, referrer?: string}} env
 * @param {readonly string[]} [allowed]
 * @returns {boolean}
 */
export function isFramingAllowed(env, allowed = ALLOWED_FRAME_ANCESTORS) {
  if (!env || !env.framed) return true;

  const ancestors = env.ancestorOrigins;
  if (Array.isArray(ancestors)) {
    if (ancestors.length === 0) return false;
    return ancestors.every((origin) => allowed.includes(origin));
  }

  const referrerOrigin = originOf(env.referrer || '');
  return referrerOrigin !== null && allowed.includes(referrerOrigin);
}

/**
 * The embedder(s) we refused, for the diagnostic message. Never rendered as
 * HTML — the value is attacker-controlled text.
 *
 * @param {{ancestorOrigins?: string[]|null, referrer?: string}} env
 * @returns {string}
 */
export function describeEmbedder(env) {
  const ancestors = env && Array.isArray(env.ancestorOrigins) ? env.ancestorOrigins : null;
  if (ancestors && ancestors.length > 0) return ancestors.join(', ');
  const referrerOrigin = originOf((env && env.referrer) || '');
  return referrerOrigin || 'unknown';
}
